
import { browser } from '$app/environment';
import {
	PUBLIC_FIREBASE_API_KEY,
	PUBLIC_FIREBASE_APP_ID,
	PUBLIC_FIREBASE_AUTH_DOMAIN,
	PUBLIC_FIREBASE_CLOUD_MESSAGING_VAPID,
	PUBLIC_FIREBASE_DATABASE_URL,
	PUBLIC_FIREBASE_MEASUREMENT_ID,
	PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
	PUBLIC_FIREBASE_PROJECT_ID,
	PUBLIC_FIREBASE_STORAGE_BUCKET
} from '$env/static/public';
import type { Analytics, AnalyticsCallOptions, EventParams } from 'firebase/analytics';
import type { FirebaseApp } from 'firebase/app';
import type { Auth, User, UserCredential } from 'firebase/auth';
import type { DocumentSnapshot, Firestore, QuerySnapshot } from 'firebase/firestore';
import type { GetTokenOptions, MessagePayload, Messaging } from 'firebase/messaging';
import type { RemoteConfig, Value } from 'firebase/remote-config';
import { Observable, catchError, from, of, switchMap } from 'rxjs';


//#######################################################################################
// MULTI-CONTEXT
//#######################################################################################

export interface IContext {
	app: FirebaseApp;

	auth?: Auth;
	signInWithCustomToken: (token: string) => Promise<UserCredential>;

	store: Firestore;
	getCollection: (path: string) => Promise<QuerySnapshot>;
	getDoc: (path: string) => Promise<DocumentSnapshot>;
	updateDoc: (path: string, update: any) => Promise<void>;
	setDoc: (path: string, update: any) => Promise<void>;
	addDoc: (path: string, update: any) => Promise<string>;
	mergeDoc: (path: string, update: any) => Promise<void>;
	delDoc: (path: string) => Promise<void>;
	onSnapshot: (path: string) => Observable<DocumentSnapshot>;

	analytics: Analytics;
	logEvent: (analyticsInstance: Analytics, eventName: string, eventParams?: EventParams, options?: AnalyticsCallOptions) => void
	
	config: RemoteConfig;
	fetchAndActivate: (remoteConfig: RemoteConfig) => Promise<boolean>;
	getAll: (remoteConfig: RemoteConfig) => Record<string, Value>;
	getValue: (key: string) => Value;

	messaging: Messaging;
	getToken: (options?: GetTokenOptions) => Promise<string>
	deleteToken: () => Promise<boolean>
	message$: Observable<MessagePayload>
	
}

const _contexts = new Map<string, IContext>();

//#######################################################################################
// App
//#######################################################################################

async function app(context?: string | IContext):Promise<IContext> {
	let ctx: IContext;
	if (!context) context = '_default_';
	if (typeof context === 'string') {
		const { initializeApp } = await import('firebase/app');
		const app = initializeApp({
			apiKey: PUBLIC_FIREBASE_API_KEY,
			authDomain: PUBLIC_FIREBASE_AUTH_DOMAIN,
			databaseURL: PUBLIC_FIREBASE_DATABASE_URL,
			projectId: PUBLIC_FIREBASE_PROJECT_ID,
			storageBucket: PUBLIC_FIREBASE_STORAGE_BUCKET,
			messagingSenderId: PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
			appId: PUBLIC_FIREBASE_APP_ID,
			measurementId: PUBLIC_FIREBASE_MEASUREMENT_ID
		});
		ctx = { app } as IContext;
		_contexts.set(context, ctx);
	} else {
		ctx = context;
	}
	return ctx;
}

//#######################################################################################
// Auth
//#######################################################################################

async function auth(context?: string | IContext) {
	if (!browser) return;
	const ctx = await app(context);
	if (ctx?.auth) return ctx;
	const { initializeAuth, signInWithCustomToken } = await import('firebase/auth');
	const auth = initializeAuth(ctx.app);
	ctx.auth = auth;
	ctx.signInWithCustomToken = (token: string) => signInWithCustomToken(auth, token);
	return ctx;
}

export async function signInWithCustomToken(token:string, context?: IContext | string) {
	const ctx = await auth(context);
	if (!ctx) return;
	return ctx.signInWithCustomToken(token);
}

export function obsAuth(context?: IContext | string) {
	return from(auth(context)).pipe(
		switchMap(ctx => new Observable<User|null>(subscriber => ctx?.auth?.onAuthStateChanged(subscriber))),
	);
}

//#######################################################################################
// Firestore
//#######################################################################################


async function store(context?: string | IContext) {
	if (!browser) return;
	const ctx = await app(context);
	if (ctx?.store) return ctx;
	const { initializeFirestore, collection, getDocs, getDoc, updateDoc, setDoc, addDoc, doc, onSnapshot, deleteDoc } = await import('firebase/firestore');
	ctx.store = initializeFirestore(ctx.app, {});
	ctx.getCollection = (path:string) => getDocs(collection(ctx.store, path));
	ctx.getDoc = (path:string) => getDoc(doc(ctx.store, path));
	ctx.updateDoc = (path: string, data: any) => updateDoc(doc(ctx.store, path), data);
	ctx.setDoc = (path: string, data: any) => setDoc(doc(ctx.store, path), data);
	ctx.addDoc = (path: string, data: any) => addDoc(collection(ctx.store, path), data).then(ref => ref.id);
	ctx.mergeDoc = (path: string, data: any) => setDoc(doc(ctx.store, path), data, {merge: true});
	ctx.delDoc = (path: string) => deleteDoc(doc(ctx.store, path));
	ctx.onSnapshot = (path: string) => new Observable<DocumentSnapshot>(subscriber => {
		return onSnapshot(
			doc(ctx.store, path),
			snapshot => subscriber.next(snapshot),
			err => subscriber.error(err),
			() => subscriber.complete()
		);
	});
	return ctx;
}

export async function getCollection(path: string, context?: IContext | string) {
	const ctx = await store(context);
	if (!ctx) return;
	try {
		return ctx.getCollection(path);
	} catch (error) {
		console.error(`Firebase getCollection "${path}" failed.`, error);
		return;
	}
}

export async function getDoc(path: string, context?: IContext | string) {
	const ctx = await store(context);
	if (!ctx) return;
	try {
		return ctx.getDoc(path);
	} catch (error) {
		console.error(`Firebase getDoc "${path}" failed.`, error);
		return;
	}
}

export function getDoc$(path: string, context?: IContext | string) {
	return from(store(context)).pipe(
		switchMap(ctx => ctx?.onSnapshot(path) ?? of(null)),
		catchError(err => {
			console.error(`Firebase getDoc$ "${path}" failed.`, err);
			return of(null);
		}),
	);
}

export async function addDoc(collectionPath: string, data: any, context?: IContext | string) {
	const ctx = await store(context);
	if (!ctx) return;
	return ctx.addDoc(collectionPath, data);
}

export async function updateDoc(path: string, data: any, context?: IContext | string) {
	const ctx = await store(context);
	if (!ctx) return;
	return ctx.updateDoc(path, data);
}

export async function setDoc(path: string, data: any, context?: IContext | string) {
	const ctx = await store(context);
	if (!ctx) return;
	return ctx.setDoc(path, data);
}

export async function mergeDoc(path: string, data: any, context?: IContext | string) {
	const ctx = await store(context);
	if (!ctx) return;
	return ctx.mergeDoc(path, data);
}

export async function delDoc(collectionPath: string, context?: IContext | string) {
	const ctx = await store(context);
	if (!ctx) return;
	return ctx.delDoc(collectionPath);
}

//#######################################################################################
// Analytics
//#######################################################################################

async function analytics(context?: string | IContext) {
	if (!browser) return;
	const ctx = await app(context);
	if (ctx?.analytics) return ctx;
	const { initializeAnalytics, logEvent } = await import('firebase/analytics');
	ctx.analytics = initializeAnalytics(ctx.app);
	ctx.logEvent = logEvent;
	return ctx;
}

export async function logNavigation(firebase_screen?: string | null, context?: string | IContext) {
	if (!firebase_screen) return;
	const ctx = await analytics(context);
	if (!ctx) return;
	ctx.logEvent(ctx.analytics, 'screen_name', { firebase_screen });
}

export async function logEvent(name: string, params?: Record<string, unknown>, context?: string | IContext) {
	const ctx = await analytics(context);
	if (!ctx) return;
	ctx.logEvent(ctx.analytics, name, params);
}

//#######################################################################################
// Remote Config
//#######################################################################################

async function config(context?: string | IContext) {
	if (!browser) return;
	const ctx = await app(context);
	if (ctx?.config) return ctx;
	const { getRemoteConfig, fetchAndActivate, getAll, getValue } = await import('firebase/remote-config');
	ctx.config = getRemoteConfig(ctx.app);
	ctx.fetchAndActivate = () => fetchAndActivate(ctx.config);
	ctx.getAll = () => getAll(ctx.config);
	ctx.getValue = (key: string) => getValue(ctx.config, key);
	return ctx;
}

export async function getValue(key: string, context?: string | IContext) {
	const ctx = await config(context);
	if (!ctx) return;
	ctx.getValue(key);
}

// EXAMPLES

export enum PromoIndicatorOption {
	None = 0,
	OnlyTopDeal = 1,
	Numbered = 2,
}

export async function getPromoIndicatorOption(context?: string | IContext) {
	const ctx = await config(context);
	if (!ctx) return;
	return ctx.getValue('web_promo_indicator_option').asNumber() as PromoIndicatorOption;
}


//#######################################################################################
// Cloud Messaging
//#######################################################################################

async function messaging(context?: string | IContext) {
	if (!browser) return;
	const ctx = await app(context);
	if (ctx?.messaging) return ctx;
	const { getMessaging, getToken, deleteToken, onMessage } = await import('firebase/messaging');
	ctx.messaging = getMessaging(ctx.app);
	ctx.getToken = (options?: GetTokenOptions) => getToken(ctx.messaging, options);
	ctx.deleteToken = () => deleteToken(ctx.messaging);
	ctx.message$ = new Observable<MessagePayload>(subscriber => {
		return onMessage(ctx.messaging, payload => {
			subscriber.next(payload);
		});
	});
	return ctx;
}

export async function getToken(context?: string | IContext) {
	const ctx = await messaging(context);
	if (!ctx) return;
	return ctx.getToken({ vapidKey: PUBLIC_FIREBASE_CLOUD_MESSAGING_VAPID });
}

export async function getMessageStream(context?: string | IContext) {
	const ctx = await messaging(context);
	if (!ctx) return;
	return ctx.message$;
}

export async function deleteToken(context?: string | IContext) {
	const ctx = await messaging(context);
	if (!ctx) return;
	return ctx.deleteToken();
}
